/**
 * Which proposal structures an RFQ can be scoped to.
 *
 * The proposal's enabled node sets are the source of truth — except that once Files
 * tracks are tagged to structures (the structure split / "set up structures" flow), a
 * node set no track references is a leftover, usually the proposal's original
 * whole-project default ("Apex Four C's Barn") that the real structures were forked
 * from. It still carries pricing, so it stays enabled on the proposal, but it is not a
 * structure suppliers can quote and must not count toward "3 of 4 structures".
 */
import type { ProjectFileStream, ProposalNodeSet } from '../types';

type StreamStructureIds = Pick<ProjectFileStream, 'structureIds'>;

/** Structure ids tagged on at least one track; `null` when no track is tagged yet. */
export function taggedStructureIds(streams: ReadonlyArray<StreamStructureIds> | null | undefined): Set<string> | null {
  const ids = new Set<string>();
  for (const s of streams ?? []) {
    for (const id of s.structureIds ?? []) if (id) ids.add(id);
  }
  return ids.size ? ids : null;
}

/**
 * Enabled node sets suppliers can quote. With tagged tracks, only node sets some track
 * references survive — unless that would leave none (ids out of sync), in which case
 * every enabled node set is kept so nothing silently disappears.
 */
export function rfqStructureNodeSets<T extends Pick<ProposalNodeSet, 'id' | 'enabled'>>(
  nodeSets: ReadonlyArray<T> | null | undefined,
  streams: ReadonlyArray<StreamStructureIds> | null | undefined,
): T[] {
  const enabled = (nodeSets ?? []).filter(ns => ns.enabled !== false);
  const tagged = taggedStructureIds(streams);
  if (!tagged) return enabled;
  const kept = enabled.filter(ns => tagged.has(ns.id));
  return kept.length ? kept : enabled;
}
